import React, { FC, Fragment } from 'react';
import { useHistory } from 'react-router';
import { Hidden, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';

import {
  UpdateUserProfileMutation,
  UpdateUserProfileOnboardingMutation,
  UpdateUserProfileMutationVariables,
  UpdateUserProfileOnboardingMutationVariables,
  UserProfileInput,
  useUpdateUserProfileMutation,
  useUpdateUserProfileOnboardingMutation,
} from 'api';
import { ClientLogo, Layouts } from 'components';
import { useProtectedClient, useFeatures, useUpdateCurrentUser } from 'hooks';
import { getFullName } from 'helpers';
import * as paths from 'paths';

import { Form, ImageUpload, Main } from '../components';

type Update = UpdateUserProfileMutation;
type UpdateVariables = UpdateUserProfileMutationVariables;
type Onboarding = UpdateUserProfileOnboardingMutation;
type OnboardingVariables = UpdateUserProfileOnboardingMutationVariables;

const FORM_HEIGHT = 450;

const Profile: FC = () => {
  const history = useHistory();
  const client = useProtectedClient();
  const { skipOnboardingSelectGroups } = useFeatures();
  const { t } = useTranslation();

  const updateOptions = useUpdateCurrentUser<Update, UpdateVariables>();
  const updateOnboardingOptions = useUpdateCurrentUser<Onboarding, OnboardingVariables>();

  const { mutate: updateUserProfileOnboardingMutation, isLoading: isSlimUpdateLoading } =
    useUpdateUserProfileOnboardingMutation({
      ...updateOnboardingOptions,
      onSuccess: () => {
        history.push(paths.index);
      },
    });

  const { mutate: updateUserProfileMutation, isLoading: isUpdateLoading } =
    useUpdateUserProfileMutation({
      ...updateOptions,
      onSuccess: handleCompleted,
    });

  const isLoading = isUpdateLoading || isSlimUpdateLoading;

  function handleCompleted() {
    if (!skipOnboardingSelectGroups) {
      return history.push(paths.onboardingFollows);
    }

    updateUserProfileOnboardingMutation({
      data: {
        onboarding_completed: true,
        last_onboarding_step: 2,
      },
    });
  }

  function handleCreate(values: UserProfileInput) {
    const { department_id, location_id, ...rest } = values;

    const variables = {
      data: {
        last_onboarding_step: 1,
        lang: i18next.language,
        department_id: department_id ? department_id : null,
        location_id: location_id ? location_id : null,
        ...rest,
      },
    };

    window.Intercom?.('update', {
      id: values.id,
      name: getFullName(values),
      job_title: values.job_title,
    });

    updateUserProfileMutation({ ...variables });
  }

  function renderSidebar() {
    return (
      <Fragment>
        <ClientLogo client={client} />
        <Typography color="textSecondary" gutterBottom variant="h4">
          {t('onboarding:sidebarProfileHeader')}
        </Typography>
        <ImageUpload />
        <div style={{ minHeight: FORM_HEIGHT }}>
          <Form onSubmit={handleCreate} working={isLoading} />
        </div>
      </Fragment>
    );
  }

  return (
    <Layouts.Full2Column
      sidebar={renderSidebar()}
      main={
        <Hidden xsDown>
          <Main />
        </Hidden>
      }
    />
  );
};

export default Profile;
